import { api, type ApiServiceResult } from '../../client';

export interface EmployeeProfile {
  uuid: string;
  email: string;
  email_verified_at: string | null;
  full_name: string;
  phone: string;
  credit_total: number;
  user_login_method: any;
  user_role: {
    uuid: string;
    name: string;
  };
  user_status: {
    uuid: string;
    name: string;
  };
  job_title: string;
  location: string;
  department: string;
  total_donated_hours: number;
  total_donation_amount: number;
  total_pending_hours: number;
  last_donation_request_date: string | null;
  donation_requests: DonationRequest[] | null;
  organization: any;
}

export interface DonationRequest {
  uuid: string;
  title?: string;
  description?: string;
  leave_hours: number;
  leave_value: number;
  employer_top_up: number;
  created_at: string;
  updated_at: string;
  status?: string;
  donation_request_status?: {
    uuid: string;
    name: string;
  };
  charity: {
    uuid: string;
    name: string;
  };
  employer: {
    uuid: string;
    name: string;
  };
}

export interface EmployerContributionStats {
  approved: number;
  pending: number;
  rejected: number;
}

/** API returns data.employerContributions; use this for the full data object */
export interface EmployerContribution {
  employerContributions?: EmployerContributionStats;
}

export interface EmployeeProfileResponse {
  success: boolean;
  message: string;
  data: EmployeeProfile | null;
}

export interface EmployerContributionResponse {
  success: boolean;
  message: string;
  data: EmployerContribution | null;
}

export interface PagingInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from?: number;
  to?: number;
}

/** Paginated API can return data as { items, paging } or legacy array */
export interface DonationRequestsResponse {
  success: boolean;
  message: string;
  data: DonationRequest[] | { items: DonationRequest[]; paging?: PagingInfo };
  paging?: PagingInfo;
  errors?: any[];
}

class EmployeeProfileService {
  private baseUrl = '/user';

  /**
   * Get employee profile by UUID
   */
  async getEmployeeProfile(uuid: string): Promise<ApiServiceResult<EmployeeProfileResponse>> {
    try {
      const response = await api.get<EmployeeProfileResponse>(`${this.baseUrl}/${uuid}`);
      
      if (response.data && response.data.success) {
        return {
          data: response.data,
          status: 200,
          message: 'Employee profile retrieved successfully'
        };
      } else {
        return {
          data: response.data,
          status: 400,
          message: response.data?.message || 'Failed to retrieve employee profile'
        };
      }
    } catch (error: any) {
              return {
          data: { success: false, message: 'Error', data: null } as EmployeeProfileResponse,
          status: 500,
          message: error.response?.data?.message || 'An error occurred while retrieving employee profile'
        };
    }
  }

  /**
   * Get employer contribution statistics for a specific employee
   */
  async getEmployeeEmployerContribution(userUuid: string): Promise<ApiServiceResult<EmployerContributionResponse>> {
    try {
      const response = await api.get<EmployerContributionResponse>(`/dashboard/admin/employee/employer-contribution?user_uuid=${userUuid}`);
      
      if (response.data && response.data.success) {
        return {
          data: response.data,
          status: 200,
          message: 'Employer contribution retrieved successfully'
        };
      } else {
        return {
          data: response.data,
          status: 400,
          message: response.data?.message || 'Failed to retrieve employer contribution'
        };
      }
    } catch (error: any) {
              return {
          data: { success: false, message: 'Error', data: null } as EmployerContributionResponse,
          status: 500,
          message: error.response?.data?.message || 'An error occurred while retrieving employer contribution'
        };
    }
  }

  /**
   * Get employee donation requests with pagination
   */
  async getEmployeeDonationRequests(userUuid: string, filters: {
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_direction?: 'asc' | 'desc';
  } = {}): Promise<ApiServiceResult<DonationRequestsResponse>> {
    try {
      const params = new URLSearchParams();
      params.append('user_uuid', userUuid);
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.per_page) params.append('per_page', filters.per_page.toString());
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);
      
      const response = await api.get<DonationRequestsResponse>(`/donation-request/employee?${params.toString()}`);
      
      if (response.data && response.data.success) {
        return {
          data: response.data,
          status: 200,
          message: 'Employee donation requests retrieved successfully'
        };
      } else {
        return {
          data: response.data,
          status: 400,
          message: response.data?.message || 'Failed to retrieve employee donation requests'
        };
      }
    } catch (error: any) {
      return {
        data: { 
          success: false, 
          message: 'Error', 
          data: [],
          paging: { current_page: 1, last_page: 1, per_page: 10, total: 0, from: 0, to: 0 },
          errors: []
        } as DonationRequestsResponse,
        status: 500,
        message: error.response?.data?.message || 'An error occurred while retrieving employee donation requests'
      };
    }
  }

  /**
   * Format date to MMM DD, YYYY format (e.g., Jul 29, 2025)
   */
  static formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch {
      return 'Invalid Date';
    }
  }

  /**
   * Format currency amount
   */
  static formatCurrency(amount: number): string {
    return `$${amount.toLocaleString()}`;
  }
}

export const employeeProfileService = new EmployeeProfileService();
export { EmployeeProfileService };
export default employeeProfileService; 